import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { supabase } from "@/lib/supabase";
import type { Job, Issue, Invoice, Payment, CustomerCredit, MaterialLineItem } from "@/data/types";
import type { MaterialKit } from "@/data/materialKit";
import type { PurchaseOrder } from "@/data/VendorPOContext";
import type { JobAllocation } from "@/data/WarehouseInventoryContext";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import type { Conversation, Message } from "@/data/communicationTypes";
import { toUiJob } from "@/mappers/job.mapper";
import type { JobRow } from "@/services/job.service";
import { isUuid } from "@/lib/jobIdentifier";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { toUiIssue } from "@/mappers/issue.mapper";
import type { IssueRow } from "@/services/issue.service";
import { toUiBuildReviewEvent } from "@/mappers/build-review.mapper";
import type { BuildReviewEventRow } from "@/services/build-review.service";
import { toUiCredit } from "@/mappers/credit.mapper";
import type { CustomerCreditRow } from "@/services/credit.service";
import { VendorPOService } from "@/services/vendor.service";
import { WarehouseService } from "@/services/warehouse.service";

// ── Local mappers ─────────────────────────────────────────────────────────────

function mapConversation(row: Record<string, unknown>): Conversation {
  return {
    id: row.id as string,
    contactName: row.contact_name as string,
    contactPhone: row.contact_phone as string,
    contactEmail: row.contact_email as string,
    linkedLeadId: row.linked_lead_id as string | undefined,
    linkedQuoteId: row.linked_quote_id as string | undefined,
    linkedJobId: row.linked_job_id as string | undefined,
    assignedRep: row.assigned_rep as string,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    lastMessagePreview: row.last_message_preview as string,
    unreadCount: row.unread_count as number,
  };
}

function mapMessage(row: Record<string, unknown>): Message {
  return {
    id: row.id as string,
    conversationId: row.conversation_id as string,
    direction: row.direction as Message["direction"],
    channel: row.channel as Message["channel"],
    body: row.body as string,
    sender: row.sender as string,
    timestamp: row.timestamp as string,
    deliveryStatus: row.delivery_status as Message["deliveryStatus"],
    callDuration: row.call_duration as number | undefined,
    callOutcome: row.call_outcome as Message["callOutcome"],
    tag: row.tag as string | undefined,
  };
}

function isTerminalMaterialReadiness(value: unknown): boolean {
  return ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(String(value ?? "").trim());
}

function suppressStaleMissingMaterials(
  materials: Job["materialsList"],
  materialReadiness: Job["materialReadiness"],
): Job["materialsList"] {
  if (!isTerminalMaterialReadiness(materialReadiness)) return materials;
  return materials.map((material) => ({
    ...material,
    missingItems: 0,
    fieldPurchase: false,
  }));
}

function deriveReadinessFromJobMaterials(
  current: Job["materialReadiness"],
  materials: Job["materialsList"],
): Job["materialReadiness"] {
  if (!materials.length) return current;
  const normalizedCurrent = String(current ?? "").trim();
  if (isTerminalMaterialReadiness(normalizedCurrent)) {
    return normalizedCurrent as Job["materialReadiness"];
  }

  const hasMissing = materials.some((m) => Math.max(0, Number(m.missingItems ?? 0)) > 0);
  if (hasMissing) return "Missing Items";

  const totalRequired = materials.reduce((acc, m) => acc + Math.max(0, Number(m.quantityRequired ?? 0)), 0);
  const totalAllocated = materials.reduce((acc, m) => acc + Math.max(0, Number(m.quantityAllocated ?? 0)), 0);
  const anyStagedOrInstalled = materials.some(
    (m) =>
      Math.max(0, Number(m.quantityStaged ?? 0)) > 0 ||
      Math.max(0, Number(m.quantityInstalled ?? 0)) > 0,
  );

  if (totalRequired > 0 && totalAllocated >= totalRequired) {
    return anyStagedOrInstalled ? "Staged for Job" : "Fully Received";
  }

  return current;
}

function mapJobMaterialRowToLine(row: Record<string, unknown>): MaterialLineItem | null {
  const sku = String(row.sku ?? "").trim();
  const itemName = String(row.item_name ?? sku).trim() || sku;
  if (!sku && !itemName) return null;
  return {
    sku,
    itemName,
    quantityRequired: Math.max(0, Number(row.quantity_required ?? 0)),
    quantityAllocated: Math.max(0, Number(row.quantity_allocated ?? 0)),
    quantityStaged: Math.max(0, Number(row.quantity_staged ?? 0)),
    quantityInstalled: Math.max(0, Number(row.quantity_installed ?? 0)),
    missingItems: Math.max(0, Number(row.missing_items ?? 0)),
    unitCost: Number(row.unit_cost_snapshot ?? row.prices_snapshot ?? 0),
    unitPrice: Number(row.unit_price_snapshot ?? row.prices_snapshot ?? 0),
    fieldPurchase: Boolean(row.field_purchase),
    inventoryAssignment: row.inventory_assignment === "Assigned" ? "Assigned" : "Stock",
    lifecycleStage: String(row.lifecycle_stage ?? "Ordered") as MaterialLineItem["lifecycleStage"],
    inventoryStatus: String(row.inventory_status ?? "In Stock") as MaterialLineItem["inventoryStatus"],
  };
}

function hydrateJobFromJobMaterials(job: Job, rows: Record<string, unknown>[]): Job {
  const canonical = rows
    .map(mapJobMaterialRowToLine)
    .filter((row): row is MaterialLineItem => Boolean(row));

  if (!canonical.length) return job;

  const materialsList = suppressStaleMissingMaterials(canonical, job.materialReadiness);
  const missingItemsList = materialsList
    .filter((m) => m.missingItems > 0)
    .map((m) => m.itemName)
    .join(", ");

  return {
    ...job,
    materialsList,
    missingItemsList: missingItemsList || "",
    materialReadiness: deriveReadinessFromJobMaterials(job.materialReadiness, materialsList),
  };
}

function mapDbInvoiceStatusToUi(row: Record<string, unknown>): Invoice["status"] {
  const balanceDue = Number(row.balance_due ?? 0);
  if (Number.isFinite(balanceDue) && balanceDue <= 0) return "paid";
  const status = String(row.status ?? "").trim().toLowerCase();
  if (status === "paid") return "paid";
  if (status === "overdue") return "overdue";
  if (status === "issued" || status === "partially_paid") return "sent";
  return "draft";
}

function mapDbInvoiceTypeToUi(raw: unknown): Invoice["type"] {
  const value = String(raw ?? "").trim().toLowerCase();
  if (value === "deposit" || value === "progress" || value === "final") return value;
  return "progress";
}

function mapDbPaymentMethodToUi(raw: unknown): Payment["method"] {
  const value = String(raw ?? "").trim().toLowerCase();
  if (value.includes("card")) return "card";
  if (value.includes("financ")) return "financing";
  return "ACH";
}

async function hydrateJobAccountingSnapshot(job: Job, jobUuid: string): Promise<Job> {
  const invoicesRes = await supabase
    .from("invoices")
    .select("id,status,total,balance_due,issued_at,due_date,paid_at,created_at")
    .eq("job_id", jobUuid)
    .neq("status", "void")
    .order("created_at", { ascending: true });

  if (invoicesRes.error || !invoicesRes.data?.length) {
    if (invoicesRes.error) console.error("useJobDetailQuery [invoices]:", invoicesRes.error);
    return job;
  }

  const invoiceIds = invoicesRes.data.map((row) => String((row as { id?: unknown }).id ?? "")).filter(Boolean);
  const [itemsRes, paymentsRes] = await Promise.all([
    supabase
      .from("invoice_items")
      .select("invoice_id,item_type")
      .in("invoice_id", invoiceIds),
    supabase
      .from("payments")
      .select("id,invoice_id,amount,method,paid_at,created_at")
      .in("invoice_id", invoiceIds),
  ]);

  if (itemsRes.error) console.error("useJobDetailQuery [invoice_items]:", itemsRes.error);
  if (paymentsRes.error) console.error("useJobDetailQuery [payments]:", paymentsRes.error);

  const typeByInvoiceId = new Map<string, string>();
  for (const row of (itemsRes.data ?? []) as Array<Record<string, unknown>>) {
    const invoiceId = String(row.invoice_id ?? "");
    const itemType = String(row.item_type ?? "");
    if (invoiceId && itemType && !typeByInvoiceId.has(invoiceId)) {
      typeByInvoiceId.set(invoiceId, itemType);
    }
  }

  const invoices: Invoice[] = ((invoicesRes.data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    id: String(row.id ?? ""),
    type: mapDbInvoiceTypeToUi(typeByInvoiceId.get(String(row.id ?? ""))),
    amount: Number(row.total ?? 0),
    status: mapDbInvoiceStatusToUi(row),
    issuedAt: String(row.issued_at ?? row.created_at ?? ""),
    dueDate: row.due_date ? String(row.due_date) : undefined,
    paidAt: row.paid_at ? String(row.paid_at) : undefined,
  }));

  const payments: Payment[] = ((paymentsRes.data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    id: String(row.id ?? ""),
    invoiceId: String(row.invoice_id ?? ""),
    amount: Number(row.amount ?? 0),
    method: mapDbPaymentMethodToUi(row.method),
    timestamp: String(row.paid_at ?? row.created_at ?? ""),
  }));

  return {
    ...job,
    financials: {
      ...job.financials,
      invoices,
      payments,
    },
  };
}

export interface JobLeadAttributionLead {
  id: string;
  sourceId: string | null;
  provider: string | null;
  leadSource: string | null;
  sourceName: string | null;
  leadSourceId: string | null;
  leadSourceLookupName: string | null;
  primaryName: string | null;
  primaryPhone: string | null;
  primaryEmail: string | null;
  projectType: string | null;
  stage: string | null;
  status: string | null;
  leadCost: number | null;
  receivedAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  createdAt: string | null;
  linkedJobId: string | null;
  linkedQuoteId: string | null;
  customerId: string | null;
}

export interface JobLeadAttribution {
  currentLead: JobLeadAttributionLead | null;
  candidateLeads: JobLeadAttributionLead[];
  linkedQuoteId: string | null;
}

const LEAD_ATTRIBUTION_SELECT = [
  "id",
  "customer_id",
  "source_id",
  "provider",
  "source_name",
  "lead_source",
  "lead_source_id",
  "primary_name",
  "primary_phone",
  "primary_email",
  "project_type",
  "stage",
  "status",
  "lead_cost",
  "received_at",
  "accepted_at",
  "rejected_at",
  "created_at",
  "linked_job_id",
  "linked_quote_id",
].join(", ");

function readString(value: unknown): string | null {
  const text = String(value ?? "").trim();
  return text ? text : null;
}

function asRecordRows(rows: unknown): Array<Record<string, unknown>> {
  return Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

type ScopedJobMetadataRow = {
  id: string;
  metadata?: Record<string, unknown> | null;
};

type ScopedJobSpecMaterialKitRow = {
  job_id: string;
  spec_ref?: string | null;
  spec_version?: string | null;
  metadata?: Record<string, unknown> | null;
};

export interface JobExecutionProof {
  id: string;
  url: string;
  created_at?: string;
  execution_id?: string;
}

function mapJobExecutionProof(row: Record<string, unknown>): JobExecutionProof {
  return {
    id: String(row.id ?? ""),
    url: String(row.url ?? row.storage_path ?? ""),
    created_at: readString(row.created_at) ?? undefined,
    execution_id: readString(row.execution_id) ?? undefined,
  };
}

function mapMaterialKitFromJobMetadata(row: ScopedJobMetadataRow): MaterialKit | null {
  const meta = row.metadata ?? {};
  const raw =
    asRecord(meta.materialKit) ??
    asRecord(meta.material_kit) ??
    asRecord(meta.jobMaterialKit) ??
    asRecord(meta.job_material_kit);
  if (!raw) return null;
  const jobId = String(raw.jobId ?? raw.job_id ?? row.id);
  const specRefRaw = asRecord(raw.specRef) ?? {};
  const specId = String(specRefRaw.specId ?? specRefRaw.spec_id ?? "").trim();
  const versionNumber = Number(specRefRaw.versionNumber ?? specRefRaw.version_number ?? 0);
  if (!jobId || !specId || Number.isNaN(versionNumber)) return null;
  const items = Array.isArray(raw.items) ? (raw.items as MaterialKit["items"]) : [];
  const override = asRecord(raw.override) ? (raw.override as MaterialKit["override"]) : undefined;
  return {
    jobId,
    specRef: { specId, versionNumber },
    items,
    override,
  };
}

function mapMaterialKitFromKitRow(row: ScopedJobSpecMaterialKitRow): MaterialKit | null {
  const raw = row.metadata ?? {};
  if (!raw || typeof raw !== "object") return null;
  const jobId = String(raw.jobId ?? raw.job_id ?? row.job_id).trim();
  const specRefRaw = asRecord(raw.specRef) ?? {};
  const specId = String(
    specRefRaw.specId ??
    specRefRaw.spec_id ??
    row.spec_ref ??
    "",
  ).trim();
  const versionNumberRaw = specRefRaw.versionNumber ?? specRefRaw.version_number ?? row.spec_version ?? 0;
  const versionNumber = Number(versionNumberRaw);
  if (!jobId || !specId || Number.isNaN(versionNumber)) return null;
  const items = Array.isArray(raw.items) ? (raw.items as MaterialKit["items"]) : [];
  const override = asRecord(raw.override) ? (raw.override as MaterialKit["override"]) : undefined;
  return {
    jobId,
    specRef: { specId, versionNumber },
    items,
    override,
  };
}

function normalizePhone(value: unknown): string | null {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits || null;
}

function mapLeadAttributionRow(
  row: Record<string, unknown>,
  leadSourceNameById: Map<string, string>,
): JobLeadAttributionLead {
  const leadSourceId = readString(row.lead_source_id);
  return {
    id: String(row.id ?? ""),
    sourceId: readString(row.source_id),
    provider: readString(row.provider),
    leadSource: readString(row.lead_source),
    sourceName: readString(row.source_name),
    leadSourceId,
    leadSourceLookupName: leadSourceId ? leadSourceNameById.get(leadSourceId) ?? null : null,
    primaryName: readString(row.primary_name),
    primaryPhone: readString(row.primary_phone),
    primaryEmail: readString(row.primary_email),
    projectType: readString(row.project_type),
    stage: readString(row.stage),
    status: readString(row.status),
    leadCost: row.lead_cost == null ? null : Number(row.lead_cost),
    receivedAt: readString(row.received_at),
    acceptedAt: readString(row.accepted_at),
    rejectedAt: readString(row.rejected_at),
    createdAt: readString(row.created_at),
    linkedJobId: readString(row.linked_job_id),
    linkedQuoteId: readString(row.linked_quote_id),
    customerId: readString(row.customer_id),
  };
}

async function fetchJobLeadAttribution(
  jobUuid: string,
  rawJob: JobRow | null,
  job: Job | null,
): Promise<JobLeadAttribution> {
  if (!rawJob || !job) {
    return { currentLead: null, candidateLeads: [], linkedQuoteId: null };
  }

  const quoteRows: Array<Record<string, unknown>> = [];
  const quoteByJobRes = await supabase
    .from("quotes")
    .select("id, lead_id, lead_source, quote_number, linked_job_id, created_at")
    .eq("linked_job_id", jobUuid)
    .order("created_at", { ascending: false });
  if (!quoteByJobRes.error && quoteByJobRes.data) {
    quoteRows.push(...asRecordRows(quoteByJobRes.data));
  }

  const convertedQuoteId = readString(job.convertedFromQuoteId);
  if (convertedQuoteId) {
    const quoteByIdRes = await supabase
      .from("quotes")
      .select("id, lead_id, lead_source, quote_number, linked_job_id, created_at")
      .eq(isUuid(convertedQuoteId) ? "id" : "quote_number", convertedQuoteId)
      .limit(1);
    if (!quoteByIdRes.error && quoteByIdRes.data) {
      quoteRows.push(...asRecordRows(quoteByIdRes.data));
    }
  }

  const linkedQuoteId = readString(quoteRows[0]?.id);
  const leadRowsById = new Map<string, Record<string, unknown>>();
  const addLeadRows = (rows?: Array<Record<string, unknown>> | null) => {
    for (const row of rows ?? []) {
      const id = readString(row.id);
      if (id) leadRowsById.set(id, row);
    }
  };

  const leadIds = Array.from(new Set(
    quoteRows
      .map((row) => readString(row.lead_id))
      .filter((id): id is string => Boolean(id)),
  ));

  if (leadIds.length > 0) {
    const leadByIdsRes = await supabase
      .from("leads")
      .select(LEAD_ATTRIBUTION_SELECT)
      .in("id", leadIds);
    if (!leadByIdsRes.error) addLeadRows(asRecordRows(leadByIdsRes.data));
  }

  const linkedJobRes = await supabase
    .from("leads")
    .select(LEAD_ATTRIBUTION_SELECT)
    .eq("linked_job_id", jobUuid)
    .order("received_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });
  if (!linkedJobRes.error) addLeadRows(asRecordRows(linkedJobRes.data));

  const customerId = readString(rawJob.customer_id);
  if (customerId) {
    const byCustomerRes = await supabase
      .from("leads")
      .select(LEAD_ATTRIBUTION_SELECT)
      .eq("customer_id", customerId)
      .order("received_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(25);
    if (!byCustomerRes.error) addLeadRows(asRecordRows(byCustomerRes.data));
  }

  const organizationId = readString(rawJob.organization_id);
  const phone = normalizePhone(job.phone);
  if (phone && organizationId) {
    const byPhoneRes = await supabase
      .from("leads")
      .select(LEAD_ATTRIBUTION_SELECT)
      .eq("organization_id", organizationId)
      .eq("primary_phone", phone)
      .order("received_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(25);
    if (!byPhoneRes.error) addLeadRows(asRecordRows(byPhoneRes.data));
  }

  const email = readString(job.email)?.toLowerCase();
  if (email && organizationId) {
    const byEmailRes = await supabase
      .from("leads")
      .select(LEAD_ATTRIBUTION_SELECT)
      .eq("organization_id", organizationId)
      .ilike("primary_email", email)
      .order("received_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(25);
    if (!byEmailRes.error) addLeadRows(asRecordRows(byEmailRes.data));
  }

  const leadSourceIds = Array.from(new Set(
    Array.from(leadRowsById.values())
      .map((row) => readString(row.lead_source_id))
      .filter((id): id is string => Boolean(id)),
  ));
  let leadSourceNameById = new Map<string, string>();
  if (leadSourceIds.length > 0) {
    const sourcesRes = await supabase
      .from("lead_sources")
      .select("id, name, code")
      .in("id", leadSourceIds);
    if (!sourcesRes.error && sourcesRes.data) {
      leadSourceNameById = new Map(
        asRecordRows(sourcesRes.data).map((row) => [
          String(row.id ?? ""),
          readString(row.name) ?? readString(row.code) ?? "Unknown source",
        ]),
      );
    }
  }

  const candidateLeads = Array.from(leadRowsById.values())
    .map((row) => mapLeadAttributionRow(row, leadSourceNameById))
    .sort((a, b) => {
      const aLinked = a.linkedJobId === jobUuid || leadIds.includes(a.id);
      const bLinked = b.linkedJobId === jobUuid || leadIds.includes(b.id);
      if (aLinked !== bLinked) return aLinked ? -1 : 1;
      const aTime = Date.parse(a.receivedAt ?? a.acceptedAt ?? a.createdAt ?? "") || 0;
      const bTime = Date.parse(b.receivedAt ?? b.acceptedAt ?? b.createdAt ?? "") || 0;
      return bTime - aTime;
    });

  const currentLead =
    candidateLeads.find((lead) => lead.linkedJobId === jobUuid) ??
    candidateLeads.find((lead) => leadIds.includes(lead.id)) ??
    null;

  return { currentLead, candidateLeads, linkedQuoteId };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface JobDetailData {
  job: Job | null;
  /** Issues for this job only (pre-filtered by job UUID) */
  jobIssues: Issue[];
  buildReviewEvents: BuildReviewEvent[];
  /** Conversations linked to this job */
  jobConversations: Conversation[];
  /** Messages across all job-linked conversations */
  jobMessages: Message[];
  /** Raw job_materials rows (TEXT job_id FK) */
  jobMaterials: Record<string, unknown>[];
  /** Raw receiving_records rows tied to this job */
  receivingRecords: Record<string, unknown>[];
  /** Raw scheduling_events rows tied to this job */
  schedulingEvents: Record<string, unknown>[];
  /** Material kit scoped to this job only. */
  jobMaterialKit: MaterialKit | undefined;
  /** Purchase orders scoped to this job only. */
  purchaseOrders: PurchaseOrder[];
  /** Warehouse allocations scoped to this job only. */
  jobAllocations: JobAllocation[];
  /** Execution proof photos for this job */
  executionProofs: JobExecutionProof[];
  /** Customer credits scoped to this job only. */
  jobCredits: CustomerCredit[];
  /** Lead/source attribution for this closed job, plus switchable candidates. */
  leadAttribution: JobLeadAttribution;
  setLeadAttribution: Dispatch<SetStateAction<JobLeadAttribution>>;
  setJob: Dispatch<SetStateAction<Job | null>>;
  loading: boolean;
  refetchJobMaterials: () => Promise<void>;
  refetchPurchaseOrders: () => Promise<void>;
  refetchJobAllocations: () => Promise<void>;
}

export function useJobDetailQuery(uuid: string | undefined): JobDetailData {
  const [job, setJob] = useState<Job | null>(null);
  const [jobIssues, setJobIssues] = useState<Issue[]>([]);
  const [buildReviewEvents, setBuildReviewEvents] = useState<BuildReviewEvent[]>([]);
  const [jobConversations, setJobConversations] = useState<Conversation[]>([]);
  const [jobMessages, setJobMessages] = useState<Message[]>([]);
  const [jobMaterials, setJobMaterials] = useState<Record<string, unknown>[]>([]);
  const [receivingRecords, setReceivingRecords] = useState<Record<string, unknown>[]>([]);
  const [schedulingEvents, setSchedulingEvents] = useState<Record<string, unknown>[]>([]);
  const [jobMaterialKit, setJobMaterialKit] = useState<MaterialKit | undefined>(undefined);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [jobAllocations, setJobAllocations] = useState<JobAllocation[]>([]);
  const [executionProofs, setExecutionProofs] = useState<JobExecutionProof[]>([]);
  const [jobCredits, setJobCredits] = useState<CustomerCredit[]>([]);
  const [leadAttribution, setLeadAttribution] = useState<JobLeadAttribution>({
    currentLead: null,
    candidateLeads: [],
    linkedQuoteId: null,
  });
  const [loading, setLoading] = useState(true);
  const genRef = useRef(0);
  // Holds the resolved execution IDs so the realtime callback can refetch
  // without re-running the full fetch waterfall.
  const executionIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const gen = ++genRef.current;
    if (!uuid) {
      setJob(null);
      setJobIssues([]);
      setBuildReviewEvents([]);
      setJobConversations([]);
      setJobMessages([]);
      setJobMaterials([]);
      setReceivingRecords([]);
      setSchedulingEvents([]);
      setJobMaterialKit(undefined);
      setPurchaseOrders([]);
      setJobAllocations([]);
      setExecutionProofs([]);
      setJobCredits([]);
      setLeadAttribution({ currentLead: null, candidateLeads: [], linkedQuoteId: null });
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const run = async () => {
      if (import.meta.env.DEV) {
        console.debug("[useJobDetailQuery] fetch", { uuid });
      }

      // If the route param is a job_number (not a UUID), resolve to the real UUID first
      let jobUuid = uuid;
      if (!isUuid(uuid)) {
        const orgId = await getCurrentOrganizationId();
        let query = supabase
          .from("jobs")
          .select("id")
          .eq("job_number", uuid);
        if (orgId) {
          query = query.eq("organization_id", orgId);
        }
        const { data } = await query.maybeSingle();
        if (data?.id) jobUuid = data.id;
      }

      if (cancelled || genRef.current !== gen) return;

      // Phase 1: all UUID-keyed tables in parallel
      const [jobRes, issuesRes, eventsRes, convsRes, proofsRes, contractAttachmentRes] = await Promise.all([
        supabase
          .from("jobs")
          .select("*, customers(first_name, last_name, city, state), contractors(company_name)")
          .eq("id", jobUuid)
          .maybeSingle(),
        supabase
          .from("issues")
          .select("*")
          .eq("job_id", jobUuid)
          .order("created_at", { ascending: false }),
        supabase
          .from("build_review_events")
          .select("id,job_id,job_number,timestamp,performed_by,action,notes,revision_notes,revision_reason,material_changes")
          .eq("job_id", jobUuid)
          .order("timestamp", { ascending: true }),
        supabase
          .from("conversations")
          .select("id,contact_name,contact_phone,contact_email,linked_lead_id,linked_quote_id,linked_job_id,assigned_rep,created_at,updated_at,last_message_preview,unread_count")
          .eq("linked_job_id", jobUuid),
        (async (jobUuid: string) => {
          try {
            const executionIds = new Set<string>();

            // Canonical linkage: execution_proofs.execution_id -> job_spec_executions.id
            const executionsRes = await supabase
              .from("job_spec_executions")
              .select("id")
              .eq("job_id", jobUuid);
            if (!executionsRes.error && executionsRes.data) {
              for (const row of executionsRes.data) {
                const id = String((row as { id?: string }).id ?? "").trim();
                if (id) executionIds.add(id);
              }
            }

            // Compatibility linkage: some historical and portal writes used jobs.id directly.
            executionIds.add(jobUuid);

            // Compatibility linkage: some historical portal shell flows used
            // installer_opportunities.id as the execution_id surrogate.
            const oppRes = await supabase
              .from("installer_opportunities")
              .select("id")
              .eq("app_job_id", jobUuid);
            if (!oppRes.error && oppRes.data) {
              for (const row of oppRes.data) {
                const id = String((row as { id?: string }).id ?? "").trim();
                if (id) executionIds.add(id);
              }
            }
            // Persist all resolved IDs so the realtime subscription can refetch proofs
            // without re-running the full fetch waterfall.
            executionIdsRef.current = executionIds;

            return await supabase
              .from("execution_proofs")
              .select("id, url:storage_path, created_at, execution_id")
              .in("execution_id", Array.from(executionIds))
              .is("superseded_by", null)
              .order("created_at", { ascending: false });
          } catch (err) {
            console.warn("Failed to fetch execution proofs:", err);
            return { data: [] as JobExecutionProof[], error: null };
          }
        })(jobUuid),
        supabase
          .from("attachments")
          .select("id, file_path, file_name, mime_type, file_size, created_at")
          .eq("parent_type", "job")
          .eq("parent_id", jobUuid)
          .eq("attachment_type", "contract")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (cancelled || genRef.current !== gen) return;

      if (jobRes.error) console.error("useJobDetailQuery [job]:", jobRes.error);
      if (issuesRes.error) console.error("useJobDetailQuery [issues]:", issuesRes.error);
      if (eventsRes.error) console.error("useJobDetailQuery [events]:", eventsRes.error);
      if (convsRes.error) console.error("useJobDetailQuery [convs]:", convsRes.error);

      const mappedJob = jobRes.data ? toUiJob(jobRes.data as JobRow) : null;
      // If the salesRep is a UUID, try to fetch the representative's name
      let jobToSet = mappedJob;
      if (mappedJob && isUuid(mappedJob.salesRep)) {
        const { data: repData } = await supabase
          .from("profiles")
          .select("first_name, last_name")
          .eq("id", mappedJob.salesRep)
          .maybeSingle();
        if (cancelled || genRef.current !== gen) return;

        if (repData) {
          const repName = [repData.first_name, repData.last_name].filter(Boolean).join(" ");
          if (repName) {
            jobToSet = {
              ...mappedJob,
              salesRep: repName,
            };
          }
        }
      }

      // If contractDocument is missing from metadata, synthesize it from the attachments table.
      // This covers old approved jobs and newly approved jobs where metadata wasn't yet fixed.
      if (jobToSet && !jobToSet.contractDocument && contractAttachmentRes?.data) {
        const att = contractAttachmentRes.data as {
          id: string; file_path: string; file_name: string | null;
          mime_type: string | null; file_size: number | null; created_at: string;
        };
        const addr = [jobToSet.address.street, jobToSet.address.city, jobToSet.address.state, jobToSet.address.zip]
          .filter(Boolean).join(", ");
        jobToSet = {
          ...jobToSet,
          contractDocument: {
            id: att.id,
            quoteId: "",
            customerName: jobToSet.customerName,
            customerEmail: jobToSet.email ?? "",
            customerAddress: addr,
            contractTotal: 0,
            termsAccepted: true,
            rescissionAcknowledged: true,
            status: "Signed",
            createdAt: att.created_at,
            filePath: att.file_path,
            fileName: att.file_name ?? undefined,
            mimeType: att.mime_type ?? undefined,
            fileSize: att.file_size ?? undefined,
          },
        };
      }

      // Fallback for native jobs: if contractDocument is still missing, pull it from the
      // linked quote's `contract` column (signed during the quote → job conversion flow).
      if (jobToSet && !jobToSet.contractDocument && jobToSet.convertedFromQuoteId) {
        const quoteRef = jobToSet.convertedFromQuoteId.trim();
        const isUuidRef = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(quoteRef);
        const quoteContractRes = await (isUuidRef
          ? supabase.from("quotes").select("contract").eq("id", quoteRef).maybeSingle()
          : supabase.from("quotes").select("contract").eq("quote_number", quoteRef).maybeSingle()
        );
        if (!cancelled && genRef.current === gen) {
          const raw = (quoteContractRes.data as { contract?: unknown } | null)?.contract;
          if (raw && typeof raw === "object") {
            jobToSet = { ...jobToSet, contractDocument: raw as Job["contractDocument"] };
          }
        }
      }

      if (jobToSet) {
        jobToSet = await hydrateJobAccountingSnapshot(jobToSet, jobUuid);
        if (cancelled || genRef.current !== gen) return;
      }

      const attribution = await fetchJobLeadAttribution(
        jobUuid,
        jobRes.data ? (jobRes.data as JobRow) : null,
        jobToSet,
      );
      if (cancelled || genRef.current !== gen) return;

      const mappedConvs = (convsRes.data ?? []).map(
        (r) => mapConversation(r as Record<string, unknown>)
      );
      const metadataKit = jobRes.data
        ? mapMaterialKitFromJobMetadata({
            id: jobUuid,
            metadata: asRecord((jobRes.data as { metadata?: unknown }).metadata),
          })
        : null;

      setJob(jobToSet);
      setLeadAttribution(attribution);
      setJobIssues((issuesRes.data ?? []).map((r) => toUiIssue(r as IssueRow)));
      setBuildReviewEvents(
        (eventsRes.data ?? []).map((r) => toUiBuildReviewEvent(r as BuildReviewEventRow))
      );
      setJobConversations(mappedConvs);
      const dedupedProofs = Array.from(
        new Map(
          asRecordRows(proofsRes.data).map((row) => [
            String(row.id ?? ""),
            mapJobExecutionProof(row),
          ]),
        ).values(),
      );
      setExecutionProofs(dedupedProofs);

      // Phase 2: messages (needs conv IDs) + materials (UUID FK), in parallel
      const convIds = mappedConvs.map((c) => c.id);

      const creditFilters = [`job_id.eq.${jobUuid}`];
      if (jobToSet?.jobNumber) creditFilters.push(`job_number.eq.${jobToSet.jobNumber}`);

      const [msgsRes, matsRes, receivingRes, schedRes, creditsRes, kitRes, scopedPurchaseOrders, scopedJobAllocations] = await Promise.all([
        convIds.length > 0
          ? supabase
              .from("messages")
              .select("id,conversation_id,direction,channel,body,sender,timestamp,delivery_status,call_duration,call_outcome,tag")
              .in("conversation_id", convIds)
              .order("timestamp", { ascending: true })
          : Promise.resolve({ data: [] as unknown[], error: null }),
        supabase.from("job_materials").select("*").eq("job_id", jobUuid),
        supabase.from("receiving_records").select("*").eq("job_id", jobUuid),
        supabase.from("scheduling_events").select("*").eq("job_id", jobUuid).order("start_at", { ascending: false }),
        supabase
          .from("customer_credits")
          .select("*")
          .or(creditFilters.join(","))
          .order("created_at", { ascending: false }),
        supabase
          .from("job_spec_material_kits")
          .select("job_id, spec_ref, spec_version, metadata")
          .eq("job_id", jobUuid)
          .limit(1)
          .maybeSingle(),
        VendorPOService.fetchPurchaseOrdersForJob(jobUuid).catch((err) => {
          console.error("useJobDetailQuery [purchase_orders]:", err);
          return [] as PurchaseOrder[];
        }),
        WarehouseService.fetchJobAllocationsForJob(jobUuid).catch((err) => {
          console.error("useJobDetailQuery [job_allocations]:", err);
          return [] as JobAllocation[];
        }),
      ]);

      if (cancelled || genRef.current !== gen) return;

      if (msgsRes.error) console.error("useJobDetailQuery [messages]:", msgsRes.error);
      if (matsRes.error) console.error("useJobDetailQuery [materials]:", matsRes.error);
      if (receivingRes.error) console.error("useJobDetailQuery [receiving]:", receivingRes.error);
      if (schedRes.error) console.error("useJobDetailQuery [scheduling_events]:", schedRes.error);
      if (creditsRes.error) console.error("useJobDetailQuery [customer_credits]:", creditsRes.error);
      if (kitRes.error) console.error("useJobDetailQuery [job_spec_material_kits]:", kitRes.error);
      const tableKit = kitRes.data
        ? mapMaterialKitFromKitRow(kitRes.data as unknown as ScopedJobSpecMaterialKitRow)
        : null;

      const materialRows = asRecordRows(matsRes.data);
      const hydratedJob = jobToSet ? hydrateJobFromJobMaterials(jobToSet, materialRows) : jobToSet;

      if (hydratedJob !== jobToSet) {
        setJob(hydratedJob);
      }
      setJobMessages(
        (msgsRes.data ?? []).map((r) => mapMessage(r as Record<string, unknown>))
      );
      setJobMaterials(materialRows);
      setReceivingRecords(asRecordRows(receivingRes.data));
      setSchedulingEvents(asRecordRows(schedRes.data));
      setJobMaterialKit(tableKit ?? metadataKit ?? undefined);
      setPurchaseOrders(scopedPurchaseOrders);
      setJobAllocations(scopedJobAllocations);
      setJobCredits(((creditsRes.data ?? []) as unknown as CustomerCreditRow[]).map(toUiCredit));
      setLoading(false);
    };
    void run();

    return () => {
      cancelled = true;
    };
  }, [uuid]);

  // Refetch execution proofs using the resolved execution IDs stored in the ref.
  // Called by the realtime subscription below whenever the portal makes changes.
  const refetchExecutionProofs = useCallback(async () => {
    const ids = Array.from(executionIdsRef.current);
    if (ids.length === 0) return;
    try {
      const res = await supabase
        .from("execution_proofs")
        .select("id, url:storage_path, created_at, execution_id")
        .in("execution_id", ids)
        .is("superseded_by", null)
        .order("created_at", { ascending: false });
      if (res.error || !res.data) return;
      const deduped = Array.from(
        new Map(
          asRecordRows(res.data).map((row) => [
            String(row.id ?? ""),
            mapJobExecutionProof(row),
          ]),
        ).values(),
      );
      setExecutionProofs(deduped);
    } catch (err) {
      console.warn("[useJobDetailQuery] realtime proofs refetch failed:", err);
    }
  }, []);

  // Realtime subscription: listen for portal-side changes to execution data
  // for this job so the Execution Integrity tab updates without a full refresh.
  // Channel name includes Date.now() so each mount gets a fresh instance —
  // Supabase caches channels by name and throws if .on() is called on an
  // already-subscribed channel (happens in React StrictMode double-invoke).
  useEffect(() => {
    if (!uuid) return;

    const jobUuid = isUuid(uuid) ? uuid : null;
    if (!jobUuid) return;

    const channel = supabase
      .channel(`job-execution-sync-${jobUuid}-${Date.now()}`)
      // Phase completions and metadata changes from the portal
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "job_spec_executions",
          filter: `job_id=eq.${jobUuid}`,
        },
        () => { void refetchExecutionProofs(); },
      )
      // Checklist item toggles from the portal
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "job_checklist_items",
          filter: `job_id=eq.${jobUuid}`,
        },
        () => { void refetchExecutionProofs(); },
      )
      // New proof photos uploaded from the portal
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "execution_proofs" },
        () => { void refetchExecutionProofs(); },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [uuid, refetchExecutionProofs]);

  const refetchJobMaterials = async () => {
    if (!uuid) return;
    const { data } = await supabase.from("job_materials").select("*").eq("job_id", uuid);
    setJobMaterials(data ?? []);
  };

  const refetchPurchaseOrders = useCallback(async () => {
    if (!uuid) return;
    const fresh = await VendorPOService.fetchPurchaseOrdersForJob(uuid).catch(() => [] as PurchaseOrder[]);
    setPurchaseOrders(fresh);
  }, [uuid]);

  const refetchJobAllocations = useCallback(async () => {
    if (!uuid) return;
    const fresh = await WarehouseService.fetchJobAllocationsForJob(uuid).catch(() => [] as JobAllocation[]);
    setJobAllocations(fresh);
  }, [uuid]);

  // Realtime subscription for purchase_orders and job_allocations so that
  // changes made from the Ordering or Warehouse pages are reflected in the
  // MaterialsGateCard without a full page reload.
  useEffect(() => {
    if (!uuid) return;
    const jobUuid = isUuid(uuid) ? uuid : null;
    if (!jobUuid) return;

    const channel = supabase
      .channel(`job-materials-sync-${jobUuid}-${Date.now()}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "purchase_orders", filter: `job_id=eq.${jobUuid}` },
        () => { void refetchPurchaseOrders(); },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "job_allocations", filter: `job_id=eq.${jobUuid}` },
        () => { void refetchJobAllocations(); },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [uuid, refetchPurchaseOrders, refetchJobAllocations]);

  return {
    job,
    jobIssues,
    buildReviewEvents,
    jobConversations,
    jobMessages,
    jobMaterials,
    receivingRecords,
    schedulingEvents,
    jobMaterialKit,
    purchaseOrders,
    jobAllocations,
    executionProofs,
    jobCredits,
    leadAttribution,
    setLeadAttribution,
    setJob,
    loading,
    refetchJobMaterials,
    refetchPurchaseOrders,
    refetchJobAllocations,
  };
}
